import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Clock, FileText, ArrowRight, LogOut, Users, Loader2, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CommitteeDashboard() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/committee/dashboard`, { withCredentials: true });
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin h-8 w-8 text-navy" /></div>;
  const m = data?.member || {};
  return (
    <div className="min-h-screen bg-slate-50" data-testid="committee-dashboard-page">
      <header className="bg-navy text-white py-4 px-6 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2"><Shield className="w-5 h-5 text-gold" /><span className="text-lg font-black">Committee Dashboard</span></div>
          <div className="text-xs opacity-80 mt-0.5">{m.name} · {m.designation}</div>
        </div>
        <button onClick={() => { logout(); navigate('/login'); }} className="text-xs px-3 py-1.5 rounded border border-white/30 flex items-center gap-1" data-testid="logout-btn"><LogOut className="w-3 h-3" /> Logout</button>
      </header>
      <main className="max-w-6xl mx-auto py-8 px-4">
        <Section title={`Pending Approvals (${data.pending.length})`} icon={Clock} testid="pending-section">
          {data.pending.length === 0 ? <Empty text="No pending approvals." /> :
            data.pending.map(e => <TenderCard key={e.tender_id} entry={e} onOpen={() => navigate(`/committee/tender/${e.tender_id}`)} status="pending" />)
          }
        </Section>
        <Section title={`Approved (${data.approved.length})`} icon={CheckCircle2} testid="approved-section">
          {data.approved.length === 0 ? <Empty text="None yet." /> :
            data.approved.map(e => <TenderCard key={e.tender_id} entry={e} onOpen={() => navigate(`/committee/tender/${e.tender_id}`)} status="approved" />)
          }
        </Section>
        <Section title={`Committee Review Failed (${data.failed.length})`} icon={XCircle} testid="failed-section">
          {data.failed.length === 0 ? <Empty text="None." /> :
            data.failed.map(e => <TenderCard key={e.tender_id} entry={e} onOpen={() => navigate(`/committee/tender/${e.tender_id}`)} status="failed" />)
          }
        </Section>
      </main>
    </div>
  );
}

function Section({ title, icon: Icon, children, testid }) {
  return (
    <div className="mb-8" data-testid={testid}>
      <h2 className="flex items-center gap-2 text-lg font-bold text-navy mb-3"><Icon className="w-5 h-5 text-gold" /> {title}</h2>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Empty({ text }) { return <div className="bg-white border border-slate-100 rounded-2xl p-6 text-sm text-slate-500">{text}</div>; }

function TenderCard({ entry, onOpen, status }) {
  const s = entry.submission || {};
  const statusColor = { pending: 'bg-amber-100 text-amber-800', approved: 'bg-green-100 text-green-800', failed: 'bg-red-100 text-red-800' }[status];
  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-5 hover:shadow-md transition-shadow cursor-pointer" onClick={onOpen} data-testid={`tender-card-${entry.tender_id}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${statusColor}`}>{status}</span>
            <span className="text-xs text-slate-500">Approvals: <strong>{s.approvals_count || 0}</strong> · Rejections: <strong>{s.rejections_count || 0}</strong> · Min needed: <strong>{s.min_approvals}</strong> of {s.total_members}</span>
          </div>
          <div className="font-bold text-navy">{entry.tender_title}</div>
          <div className="text-xs text-slate-600 mt-0.5">{entry.work_category} · Recommended: {s.recommended_supplier_name || '—'} · {s.recommended_amount ? `₹${Number(s.recommended_amount).toLocaleString('en-IN')}` : '—'}</div>
          {entry.my_decision && <div className="text-xs mt-2"><strong className={entry.my_decision.decision === 'approve' ? 'text-green-700' : 'text-red-700'}>Your decision: {entry.my_decision.decision.toUpperCase()}</strong> on {new Date(entry.my_decision.timestamp).toLocaleString('en-IN')}</div>}
        </div>
        <ArrowRight className="w-5 h-5 text-slate-400" />
      </div>
    </div>
  );
}
